import type { LngLat } from 'mapbox-gl';
import type FeatureData from './FeatureData';
import GeoBounds from './GeoBounds';
import type { GeoFieldBounds } from './GeoBounds';
import GeoLocation from './GeoLocation';
import type { GeoFieldPoint } from './GeoLocation';
import { dataTypes } from "../common/Constants";
import i18n from "i18next";
import { LocaleService } from "../common/LocaleService";

/** Request properties the card was built from. */
export interface CardProperties {
    name?: string;
    url?: string;
    [key: string]: unknown;
}

/** City reference of a POI. */
export interface CardCity {
    uri: string;
    name: string;
    id: string;
}

/** POI rating, as stored in field_poi_rating. */
export interface CardRating {
    value: string;
}

/** External link of a POI. */
export interface CardPoiUrl {
    url?: string;
    title: string | null;
}

/**
 * Raw object as returned by the REST API.
 *
 * Only the fields read by this class are declared; every subclass narrows the
 * extra ones it needs, so the rest stays unknown rather than invented.
 */
/** Servicio de un alojamiento o localidad, tal cual llega del backend. */
export interface ServicioData {
    name?: string;
    icon_html?: string;
}

export interface CardRawData {
    nid?: number | string;
    tid?: number | string;
    type?: string;
    title?: string;
    url?: string;
    geo_location?: GeoFieldPoint;
    geo_bounds?: GeoFieldBounds;
    booking_url?: string;
    extra_html?: string;
    image_main?: {
        image_html?: string;
        /** Variantes responsive por clave de estilo (wide_640, rect_940...). */
        image_styles?: Record<string, string>;
        /** 'booking' cuando la imagen viene de Booking.com. */
        image_source?: string;
    };
    poi_imagen?: { file?: { url?: string } };
    status_warning?: string;
    nota_destacada?: string;
    [key: string]: unknown;
}

/**
 * CardData - Data from REST service.
 *
 * Data wrapper for object types:
 * - Accommodation (Hotel, Hostel)
 * - Place (City, Monument)
 */
class CardData {
    static readonly BIG = 'big';
    static readonly SMALL = 'small';

    static readonly WARNING_INFO = 'info';
    static readonly WARNING_CLOSED = 'closed';

    properties: CardProperties = {
        name: '',
        url: '',
    };

    /**
     * Object type.
     */
    type: string;
    /**
     * Object Id.
     */
    id?: number;

    link: string | null | undefined = '';
    title?: string;
    subtitle?: string;

    imgHTML: string | null = '';
    extra_html: string | undefined = '';
    texto_intro: string | undefined = '';

    // stage properties
    lengthKm = '-';
    duration = '-';
    difficulty = 0;
    landscape = 0;
    path_steps: unknown = [];
    elevationImage: string | null | undefined = null;

    // Geo properties.
    geo_location?: GeoFieldPoint;
    geo_bounds?: GeoFieldBounds;

    // Place / Hotel
    /**
     * Servicios del alojamiento o la localidad.
     *
     * OJO: por defecto es la cadena vacia, no un array. Es lo que distingue
     * "sin servicios" en el render de CardInfo, que comprueba la verdad del
     * campo antes de recorrerlo: un [] seria veraz y pintaria el contenedor
     * vacio.
     */
    tag_servicios: ServicioData[] | '' = '';
    bookingUrl: string | undefined = '';
    phoneNumber = '';
    notaDestacada = '';

    // POI
    rating?: CardRating;
    city: CardCity = {
        uri: '',
        name: '',
        id: '',
    }
    poiUrl?: CardPoiUrl;

    // Raw object data
    data?: CardRawData;

    // Selected card state.
    selected = false;

    // Coordenadas geograficas del punto que abrio la tarjeta.
    // @see MapService.getTranslationToCenter, que lee .lng y .lat
    clickPoint?: LngLat | null;

    constructor(type: string, properties: CardProperties, data?: CardRawData) {
        this.type = type;
        this.title = properties.name;
        this.properties = properties;

        if (data) {
            // The id will be node or term id.
            if (data.nid) {
                this.id = parseInt(String(data.nid));
            }
            else if (data.tid) {
                this.id = parseInt(String(data.tid));
            }
            this.title = data.title;
            this.link = data.url;

            this.geo_location = data.geo_location;
            this.bookingUrl = data.booking_url;

            this.extra_html = data.extra_html;
            if (data.type !== dataTypes.poi) {
                // Fuera de los POI el backend siempre envia image_main; si
                // faltase, esta linea ya lanzaba antes de tipar el fichero.
                const imageMain = data.image_main!;
                this.imgHTML = imageMain.image_html ? imageMain.image_html : null;
            } else if (data.poi_imagen && data.poi_imagen.file) {
                this.imgHTML = `<img src=${data.poi_imagen?.file.url} />`;
            }

            this.data = data;
        }
    }

    /**
     * Gets card title.
     */
    getTitle(): string | undefined {
        return this.title ? this.title : this.properties.name;
    }

    /**
     * Gets unique Map id.
     * Examples: place[id], hotel[id], track_id, route_id
     *
     * Abstract in practice: RouteCard, HotelCard, PlaceCard and StageCard
     * override it. PoiCard and OfflineCard do not and rely on undefined, so the
     * empty body must stay.
     */
    getMapId(): string | null | undefined {
        return undefined;
    }

    /**
     * Gets card subtitle
     */
    getSubtitle(): string | undefined {
        return this.subtitle ? this.subtitle : '';
    }

    /**
     * Gets card url/link.
     */
    getUrl(): string | null | undefined {
        return this.link ? this.link : this.properties.url;
    }

    /**
     * Gets card type.
     */
    getType(): string {
        return this.type ? this.type : '';
    }

    /**
     * Gets geo bounds.
     */
    getGeoLocation(): GeoLocation | null {
        // lon/lat llegan como cadena desde el backend; LngLat ya los convertia.
        return this.geo_location
            ? new GeoLocation(Number(this.geo_location.lon), Number(this.geo_location.lat))
            : null;
    }

    /**
     * Gets geo bounds.
     */
    getGeoBounds(): GeoBounds | null | undefined {
        return this.data && this.data.geo_bounds ? GeoBounds.fromGeoData(this.data.geo_bounds) : null;
    }

    /**
     * Gets detail card size for this data.
     */
    getDetailCardSize(): string {
        if (this.getType() === dataTypes.stage)
            return CardData.BIG;
        return this.imgHTML ? CardData.BIG : CardData.SMALL;
    }

    /**
     * Navigation: has next?.
     *
     * Abstract in practice: only StageCard overrides it, the rest rely on the
     * undefined this returns.
     */
    hasNext(): boolean | undefined {
        return undefined;
    }

    /**
     * Navigation: has previous?.
     *
     * @see hasNext
     */
    hasPrev(): boolean | undefined {
        return undefined;
    }

    /**
     * Navigation: Get next.
     */
    getNext(): FeatureData | null | undefined {
        return undefined;
    }

    /**
     * Navigation: Get previous.
     */
    getPrev(): FeatureData | null | undefined {
        return undefined;
    }

    /**
     * Get warning text.
     */
    getWarningText(): string {
        let text = '';
        if (this.data) {
            if (this.data.status_warning) {
                if (this.data.status_warning.toLowerCase() === 'cerrado temporalmente') {
                    text = i18n.t('cardContent.hotel.warning_closed');
                } else {
                    text = LocaleService.decodeHtmlEntities(this.data.status_warning);
                }
            }
            if (this.data.nota_destacada) {
                text = LocaleService.decodeHtmlEntities(this.data.nota_destacada);
            }
        }
        return text;
    }

    /**
     * Get warning type: info, closed, etc..
     */
    getWarningType(): string {
        return CardData.WARNING_INFO;
    }

    /**
     * Whether this card can be selected.
     */
    canSelect(): boolean {
        return false;
    }

    /**
     * Whether card has select buttons.
     */
    hasSelectionButtons(): boolean | undefined {
        return this.canSelect() || this.hasNext() || this.hasPrev();
    }
}

export default CardData;
